import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { apiClient } from '../services/apiClient';
import { brandConfig } from '../config/brands';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './CreatorProfilePage.css';

type CreatorTemplate = {
  label: string;
  thumbnail_url: string | null;
  disclosure: 'private' | 'teaser' | 'full';
  teaser: string | null;
  prompt: string | null;
};

type CreatorProfile = {
  slug: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  rank_code: string;
  rank_label: string | null;
  links: Partial<Record<'website' | 'x' | 'note' | 'instagram' | 'youtube' | 'line' | 'tiktok' | 'facebook', string>>;
  templates: CreatorTemplate[];
};

const LINK_LABELS: Record<string, string> = {
  website: 'Webサイト', x: 'X', note: 'note', instagram: 'Instagram',
  youtube: 'YouTube', line: 'LINE', tiktok: 'TikTok', facebook: 'Facebook',
};

export default function CreatorProfilePage() {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['public-creator', slug],
    enabled: !!slug,
    retry: false,
    // partner_code はパートナー面の可視テンプレ解決に必須 (バックエンドで fail-closed)。
    queryFn: async () =>
      (await apiClient.get<CreatorProfile>(
        `/public/creators/${slug}?partner_code=${encodeURIComponent(brandConfig.id)}`,
      )).data,
  });

  // 取得前は undefined → routes.tsx の handle ('クリエイター') が使われる
  useDocumentTitle(data?.display_name);

  const notFound = isError && (error as { response?: { status?: number } })?.response?.status === 404;

  if (isLoading) {
    return <div className="cp-page"><p className="cp-status">読み込み中…</p></div>;
  }
  if (notFound) {
    return (
      <div className="cp-page">
        <div className="cp-empty">
          <h1 className="cp-empty-title">クリエイターが見つかりません</h1>
          <p className="cp-empty-desc">URL が正しいかご確認ください。</p>
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return <div className="cp-page"><p className="cp-status">読み込みに失敗しました。時間をおいて再度お試しください。</p></div>;
  }

  const links = Object.entries(data.links).filter(([, url]) => !!url);

  return (
    <div className="cp-page">
      <header className="cp-header">
        <div className="cp-avatar">
          {data.avatar_url ? (
            <img src={data.avatar_url} alt="" />
          ) : (
            <span className="cp-avatar-fallback">{data.display_name.slice(0, 1)}</span>
          )}
        </div>
        <div className="cp-headline">
          <div className="cp-name-row">
            <h1 className="cp-name">{data.display_name}</h1>
            {data.rank_label && (
              <span className={`cp-rank cp-rank--${data.rank_code}`}>{data.rank_label}</span>
            )}
          </div>
          {data.bio && <p className="cp-bio">{data.bio}</p>}
          {links.length > 0 && (
            <div className="cp-links">
              {links.map(([key, url]) => (
                <a key={key} className="cp-link" href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} />
                  <span>{LINK_LABELS[key] ?? key}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </header>

      <section className="cp-templates">
        <h2 className="cp-section-title">提供テンプレート</h2>
        {data.templates.length === 0 ? (
          <p className="cp-status">公開中のテンプレートはありません。</p>
        ) : (
          <ul className="cp-template-list">
            {data.templates.map((t, i) => (
              <li key={i} className="cp-template">
                <div className="cp-template-head">
                  {t.thumbnail_url && (
                    <img className="cp-template-thumb" src={t.thumbnail_url} alt="" loading="lazy" />
                  )}
                  <span className="cp-template-label">{t.label}</span>
                </div>
                {t.disclosure === 'teaser' && t.teaser && (
                  <p className="cp-template-teaser">{t.teaser}</p>
                )}
                {t.disclosure === 'full' && t.prompt && (
                  <pre className="cp-template-prompt">{t.prompt}</pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
